import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { getSalesAdvice } from '../services/geminiService';
import { ApiService } from '../services/api';
import { Message } from '../types';
import { Bot } from 'lucide-react';

const SalesChat: React.FC = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState<Message[]>([
        { role: 'model', text: "Hoi! 👋 Ich bin dein Copilot für Streckenfliegen.app. Ich kann dir helfen, das perfekte Abo zu finden oder Fragen zu den Funktionen zu beantworten. Was möchtest du wissen?" }
    ]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const scrollRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages, isOpen]);

    const handleSend = async (e?: React.FormEvent) => {
        e?.preventDefault();
        if (!input.trim() || isLoading) return;

        const userMsg = input.trim();
        setInput('');
        setMessages(prev => [...prev, { role: 'user', text: userMsg }]);
        setIsLoading(true);

        try {
            // Load Quicktips for Context (if not already loaded)
            let quickTipsContext = [];
            try {
                // Optimization: In real app, maybe load this once on mount or use a context
                // For now, we fetch it here to ensure freshness or use cached if available in a real state store
                const sysLists = await ApiService.getSystemLists('config');
                const tipsList = sysLists.find((l: any) => l.key === 'quicktips');
                if (tipsList && tipsList.value) {
                    quickTipsContext = tipsList.value;
                }
            } catch (e) { console.warn("Could not load quicktips context", e); }

            const res = await getSalesAdvice(userMsg, messages, quickTipsContext);
            setMessages(prev => [...prev, { role: 'model', text: res.text }]);
        } catch (error) {
            setMessages(prev => [...prev, { role: 'model', text: "Entschuldigung, ich bin gerade überlastet. Probier es später nochmal! 😅" }]);
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) {
        return (
            <div
                className="fixed bottom-6 right-6 z-40 bg-slate-900 border border-sky-500/30 p-4 rounded-2xl shadow-2xl max-w-xs cursor-pointer hover:scale-105 transition-transform animate-in slide-in-from-right-10 fade-in duration-1000 delay-1000"
                onClick={() => setIsOpen(true)}
            >
                <div className="flex items-start gap-4 pointer-events-none">
                    <div className="w-10 h-10 bg-sky-600 rounded-full flex items-center justify-center text-white text-xl shadow-lg shrink-0 animate-pulse"><Bot className="w-4 h-4 inline mr-1" /></div>
                    <div>
                        <h4 className="text-white font-bold text-sm">Fragen zur App?</h4>
                        <p className="text-xs text-slate-400 mt-1">Lass uns chatten! Ich erkläre dir die Vorteile von Pro.</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed bottom-6 right-6 z-50 w-full max-w-sm sm:w-[400px] h-[500px] bg-slate-950 border border-sky-500/30 rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-in zoom-in-95 origin-bottom-right select-text">
            {/* Header */}
            <div className="bg-sky-900/20 p-4 border-b border-white/5 flex justify-between items-center">
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-sky-600 rounded-full flex items-center justify-center text-white text-sm shadow-lg"><Bot className="w-4 h-4 inline mr-1" /></div>
                    <div>
                        <h3 className="text-white font-bold text-sm">Sales Copilot</h3>
                        <div className="text-[10px] text-sky-400 font-medium">Online & Ready</div>
                    </div>
                </div>
                <button onClick={() => setIsOpen(false)} className="text-slate-400 hover:text-white transition-colors text-xl">×</button>
            </div>

            {/* Chat Area */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-900/50 custom-scrollbar">
                {messages.map((m, i) => (
                    <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${m.role === 'user' ? 'bg-sky-600 text-white rounded-tr-none' : 'bg-slate-800 text-slate-200 border border-slate-700 rounded-tl-none leading-relaxed'}`}>
                            {m.role === 'model' ? (
                                <div className="prose prose-invert prose-sm max-w-none marker:text-slate-400">
                                    <ReactMarkdown>{m.text}</ReactMarkdown>
                                </div>
                            ) : (
                                <div className="whitespace-pre-wrap">{m.text}</div>
                            )}
                        </div>
                    </div>
                ))}
                {isLoading && (
                    <div className="flex justify-start">
                        <div className="bg-slate-800 text-slate-500 rounded-2xl rounded-tl-none px-4 py-2 text-xs italic border border-slate-700">
                            Tippt...
                        </div>
                    </div>
                )}
            </div>

            {/* Input Area */}
            <form onSubmit={handleSend} className="p-3 border-t border-white/5 bg-slate-950 flex gap-2">
                <input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Frag mich etwas..."
                    className="flex-grow px-4 py-2 bg-slate-900 border border-slate-800 rounded-xl text-sm text-white focus:ring-2 focus:ring-sky-600 outline-none placeholder:text-slate-600"
                    autoFocus
                />
                <button type="submit" disabled={isLoading || !input.trim()} className="bg-sky-600 hover:bg-sky-500 disabled:opacity-50 disabled:cursor-not-allowed p-2.5 rounded-xl transition-all shadow-lg shadow-sky-900/40 text-white flex items-center justify-center">
                    ➤
                </button>
            </form>
        </div>
    );
};

export default SalesChat;
